// lanes - which runner kind an analysis ran on, and what to call it on screen.
//
// The platform has two lanes. A file submission detonates on a virtual machine
// with a guest agent watching it. A URL submission is fetched inside an
// analysis container by an analyzer image. They produce different evidence, so
// almost every surface that describes a run has to know which one it is looking
// at, and the vocabulary has to be stated once rather than re-derived per page.
//
// The lane is carried on the wire. Every submission row, dashboard and /v1
// alike, carries `lane`, written once at intake by `models.LaneForTarget` and
// read off the task from then on. This module only names and labels the
// values. It does not derive a lane from `target_kind`, and nothing else in the
// SPA should either: the target kind says what was submitted, the lane says
// what ran it, and a later target kind may share a lane with an existing one.
//
// The fleet views still read `machines.kind` rather than coming through here,
// and should: they describe what a run LANDED on. This describes what the run
// IS, which is what every surface about a submission is about.
//
// No imports: api.ts re-exports the constants here, so putting them anywhere
// that imports it would be a cycle.

/** The lane of a virtual-machine run. Matches `tasks.lane` and `machines.kind`. */
export const VM_LANE = 'vm';
/** The lane of an analysis-container run. Matches `tasks.lane` and `machines.kind`. */
export const CONTAINER_LANE = 'container';
/** Every lane the platform knows, in the order surfaces should offer them. */
export const PA_LANES = [VM_LANE, CONTAINER_LANE] as const;

/**
 * What each lane is called on screen. A runner kind is the platform's word for
 * it, which is right in the database and wrong in a heading.
 */
const LANE_LABELS: Record<string, string> = {
  [VM_LANE]: 'Virtual machine',
  [CONTAINER_LANE]: 'Container',
};

/** The display name of a lane. An unknown lane names itself rather than
 *  borrowing a label - the vocabulary lives in the database and this list can
 *  lag it. */
export function laneLabel(lane: string): string {
  return LANE_LABELS[lane] ?? lane;
}

/**
 * Whether a run is in an analysis container rather than on a guest.
 *
 * Only the container lane is. A lane this build has never heard of is not,
 * which is the safe direction: reading an unknown lane as a container would
 * strip the telemetry and network panes off a report that has them, and an
 * empty string is what a row older than the column normalises to.
 */
export function isContainerLane(lane: string): boolean {
  return lane === CONTAINER_LANE;
}

/**
 * The lanes a set of post-analysis stages actually uses, in the platform's own
 * order, so a lane selector never offers an empty graph.
 *
 * A lane nothing here knows about still gets an entry rather than having its
 * stages hidden. `pa_jobs.applies_to` is database vocabulary and PA_LANES can
 * lag it.
 */
export function lanesPresent(stages: readonly { applies_to: string[] }[]): string[] {
  const seen = new Set(stages.flatMap((s) => s.applies_to));
  const known = PA_LANES.filter((l) => seen.has(l));
  const extra = [...seen].filter((l) => !PA_LANES.includes(l as never)).sort();
  return [...known, ...extra];
}
